/**
 * Photo intake.
 *
 * A phone camera file is 3–8 MB of pixels nobody will ever see at that size: the
 * largest this app renders a product photo is a few hundred CSS pixels. So the
 * browser downscales and re-encodes BEFORE the upload — the request goes from
 * megabytes to tens of kilobytes, and a warehouse on a weak connection can still
 * add stock.
 *
 * Returns a `data:` URL, which is what the server's uploadImage action takes.
 */
const MAX_EDGE = 900;
const QUALITY = 0.82;
const MAX_SOURCE_BYTES = 25 * 1024 * 1024;

export const ACCEPTED_TYPES = 'image/jpeg,image/png,image/webp,image/gif,image/avif';

export function isImage(file) {
  return !!file && typeof file.type === 'string' && file.type.startsWith('image/');
}

/** Read a File into an HTMLImageElement, cleaning up the object URL after. */
function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('That file could not be read as an image'));
    };
    img.src = url;
  });
}

/**
 * Downscale to fit MAX_EDGE and re-encode as JPEG (WebP where the browser
 * writes it). A file that is already small enough is still re-encoded — it costs
 * one paint and removes the EXIF payload that would otherwise travel with it.
 */
export async function fileToDataUrl(file) {
  if (!isImage(file)) throw new Error('Choose an image file (JPEG, PNG, WebP, GIF or AVIF)');
  if (file.size > MAX_SOURCE_BYTES) throw new Error('That image is larger than 25 MB');

  const img = await loadImage(file);
  const scale = Math.min(1, MAX_EDGE / Math.max(img.naturalWidth, img.naturalHeight));
  const width = Math.max(1, Math.round(img.naturalWidth * scale));
  const height = Math.max(1, Math.round(img.naturalHeight * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, width, height);

  // A PNG with transparency would get a black backdrop from toDataURL('jpeg'),
  // so anything that might be transparent is kept as PNG.
  const type = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
  return canvas.toDataURL(type, QUALITY);
}
